import { useEffect, useRef, useState } from 'react'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import { fetchRoomMessages, type RoomConversation } from '~/data/interact'
import { parseApiTime } from '~/data/meetings'
import type { WorldSnapshot } from '~/data/types'
import type { EventBus } from '~/realtime/bus'
import { theme } from '~/theme'

// Day replay: recent REAL activity (24h, widening to 7 days on quiet days)
// compressed into a ~60s time-lapse.
// Events are replayed through the normal animation bus, flagged ambient so a
// replayed message is a transient visit (never a permanent seat move) and the
// live-only ticker ignores it. The office "wakes up" and re-performs its day.

interface ReplayEvent {
  atMs: number
  pulseId: string
  userId: string | null
  text: string | null
  topicId: string | null
}

const PLAYBACK_MS = 60_000
// Prefer the last day; on quiet days (weekends) widen so the button still
// delivers a show instead of apologising.
const WINDOWS = [
  { ms: 24 * 3600_000, label: 'the last 24h' },
  { ms: 7 * 24 * 3600_000, label: 'the last 7 days' },
]

export function ReplayControl({
  bus,
  snapshot,
  organizationId,
  getToken,
  buttonStyle,
}: {
  bus: EventBus
  snapshot: WorldSnapshot
  organizationId: string
  getToken: GetToken
  buttonStyle: React.CSSProperties
}) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'empty'>('idle')
  const [progress, setProgress] = useState(0)
  const [simLabel, setSimLabel] = useState('')
  const [windowLabel, setWindowLabel] = useState(WINDOWS[0].label)
  const timersRef = useRef<number[]>([])
  const rafRef = useRef<number>()

  const stop = () => {
    for (const t of timersRef.current) window.clearTimeout(t)
    timersRef.current = []
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    setState('idle')
  }
  // Clean up on unmount / org switch.
  useEffect(() => stop, [organizationId])

  const play = async () => {
    if (state === 'loading' || state === 'playing') return
    setState('loading')
    try {
      const client = createClient(await getToken())
      // The day's real messages across the most recently active team rooms.
      const rooms = snapshot.pulses
        .filter((p) => p.category === 'TEAM' && p.memberUserIds.length > 0)
        .sort((a, b) => {
          const ta = a.latestUpdate ? parseApiTime(a.latestUpdate) || 0 : 0
          const tb = b.latestUpdate ? parseApiTime(b.latestUpdate) || 0 : 0
          return tb - ta
        })
        .slice(0, 10)
      // Page back until we're past the widest replay window (capped): one
      // newest-slice page silently truncated a busy room's morning while the
      // bar still claimed "the last 24h".
      const oldestWantedMs = Date.now() - WINDOWS[WINDOWS.length - 1].ms
      const perRoom = await Promise.all(
        rooms.map(async (room) => {
          try {
            const all: RoomConversation['messages'] = []
            for (let page = 1; page <= 5; page += 1) {
              const conv = await fetchRoomMessages(client, {
                pulseId: room.id,
                organizationId,
                page,
              })
              all.push(...conv.messages)
              const oldest = conv.messages[0]
              if (!conv.hasMorePages) break
              if (oldest && (parseApiTime(oldest.createdAt) || 0) < oldestWantedMs) break
            }
            return all
              .map((m) => ({
                atMs: parseApiTime(m.createdAt),
                pulseId: room.id,
                userId: m.userId,
                text: m.text,
                topicId: m.topicId,
              }))
              .filter((e) => Number.isFinite(e.atMs))
          } catch {
            return [] as ReplayEvent[]
          }
        }),
      )
      const all = perRoom.flat().sort((a, b) => a.atMs - b.atMs)
      // Narrowest window with enough material wins.
      const now = Date.now()
      let events: ReplayEvent[] = []
      let picked = WINDOWS[0]
      for (const w of WINDOWS) {
        events = all.filter((e) => e.atMs >= now - w.ms)
        picked = w
        if (events.length >= 3) break
      }
      if (events.length < 3) {
        setState('empty')
        window.setTimeout(() => setState('idle'), 3500)
        return
      }
      setWindowLabel(picked.label)
      const multiDay = picked.ms > 25 * 3600_000

      // Compress the real span into the playback window.
      const startMs = events[0].atMs
      const endMs = events[events.length - 1].atMs
      const span = Math.max(endMs - startMs, 60_000)
      const scale = PLAYBACK_MS / span
      setState('playing')
      setProgress(0)

      for (const e of events) {
        const delay = Math.max(0, (e.atMs - startMs) * scale)
        timersRef.current.push(
          window.setTimeout(() => {
            bus.emit({
              kind: 'message',
              pulseId: e.pulseId,
              userId: e.userId,
              text: e.text,
              topicId: e.topicId,
              // Replay is theatre: transient visits only, ticker unaffected.
              ambient: true,
            })
          }, delay),
        )
      }

      const playStart = performance.now()
      const tickClock = () => {
        const t = Math.min(1, (performance.now() - playStart) / PLAYBACK_MS)
        setProgress(t)
        const sim = new Date(startMs + span * t)
        const hhmm = `${String(sim.getHours()).padStart(2, '0')}:${String(sim.getMinutes()).padStart(2, '0')}`
        setSimLabel(
          multiDay
            ? `${sim.toLocaleDateString(undefined, { weekday: 'short' })} ${hhmm}`
            : hhmm,
        )
        if (t < 1) rafRef.current = requestAnimationFrame(tickClock)
        else {
          timersRef.current.push(window.setTimeout(() => setState('idle'), 2500))
        }
      }
      rafRef.current = requestAnimationFrame(tickClock)
    } catch {
      setState('idle')
    }
  }

  return (
    <>
      <button
        onClick={() => (state === 'playing' ? stop() : void play())}
        title="Replay recent real activity as a time-lapse (last 24h, or the week when the day is quiet)"
        style={{
          ...buttonStyle,
          ...(state === 'playing'
            ? { color: '#ffd86b', borderColor: '#4a3d1e', background: '#241f12' }
            : null),
        }}
      >
        {state === 'loading' ? '…' : state === 'playing' ? 'Stop' : 'Replay'}
      </button>

      {state === 'playing' && (
        <div style={replayBar}>
          <span style={{ fontSize: 11, color: '#ffd86b', fontWeight: 700 }}>
            ⏵ replaying {windowLabel}
          </span>
          <div style={track}>
            <div style={{ ...fill, width: `${Math.round(progress * 100)}%` }} />
          </div>
          <span style={{ fontSize: 11, color: theme.cream, fontVariantNumeric: 'tabular-nums' }}>
            {simLabel}
          </span>
        </div>
      )}
      {state === 'empty' && (
        <div style={replayBar}>
          <span style={{ fontSize: 11, color: theme.textDim }}>
            Not enough recent activity to replay.
          </span>
        </div>
      )}
    </>
  )
}

const replayBar: React.CSSProperties = {
  position: 'absolute',
  top: 64,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 14px',
  background: '#15161de6',
  border: `1px solid ${theme.border}`,
  borderRadius: 999,
  pointerEvents: 'none',
  zIndex: 5,
}
const track: React.CSSProperties = {
  width: 160,
  height: 4,
  borderRadius: 2,
  background: '#262a36',
  overflow: 'hidden',
}
const fill: React.CSSProperties = {
  height: '100%',
  borderRadius: 2,
  background: '#ffd86b',
  transition: 'width 120ms linear',
}
